import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { Drawer, Button, Pagination, DatePicker, message } from 'antd';
import "../styles/Scorecard.css";

const URL = process.env.REACT_APP_API_URL;
const RecruiterScorecard = () => {
  const [data, setData] = useState([]);
  const [overallData, setOverallData] = useState([]);
  const [selectedHrEmail, setSelectedHrEmail] = useState('');
  const [selectedHrName, setSelectedHrName] = useState('');
  const [hrCounts, setHrCounts] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [hrWiseCandidates, setHrWiseCandidates] = useState([]);
  const [allCandidates, setAllCandidates] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(5); // Show 5 HR cards per page
  const paginatedHrCounts = hrCounts.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const { RangePicker } = DatePicker;

  const handleSelectHr = (fullName, email) => {
    setSelectedHrName(fullName);
    setSelectedHrEmail(email);
    fetchData(fullName);
    fetchCandidatesByHR(fullName);
    setStartDate(null);
    setEndDate(null);
  };

  useEffect(() => {
    setDateRange([null, null]);
  }, [selectedHrName]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    fetchAllData();
    fetchAllCandidates();
  }, []);

  const fetchHrCounts = async () => {
    try {
      const response = await axios.get(`${URL}/api/hrs/candidate-count`); // Endpoint to get HR names and candidate counts
      const sortedHrCounts = response.data.hrCounts.sort((a, b) => b.candidateCount - a.candidateCount); // Sort HRs by candidate count (highest first)
      setHrCounts(sortedHrCounts);
      console.log(sortedHrCounts);

      // if (sortedHrCounts.length > 0) {
      //   const topHr = sortedHrCounts[0]; // Get the HR with the highest count
      //   setSelectedHrName(topHr._id);
      //   setSelectedHrEmail(topHr.email);

      //   // Delay fetching data to allow chart animation
      //   setTimeout(() => {
      //     fetchData(topHr._id); // Fetch data for the top HR
      //   }, 500);  // Small delay for smoother animation
      // }
    } catch (error) {
      console.error('Error fetching HR counts:', error);
    }
  };

  const fetchAllData = async (startDate, endDate) => {
    try {
      const response = await axios.get(`${URL}/api/all/status-count`, {
        params: { startDate: startDate, endDate: endDate }  // Pass as query parameters
      });
      setOverallData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchAllCandidates = async (startDate, endDate) => {
    const response = await axios.get(`${URL}/api/getAllCandidates`, {
      params: {
        startDate: startDate,
        endDate: endDate
      }
    });
    setAllCandidates(response.data);
    // setData(response.data);
  }

  const fetchCandidatesByHR = async (mgr, startDate, endDate) => {
    try {
      const response = await axios.get(`/api/candidates/hrwise`, {
        params: {
          mgr: mgr,
          startDate: startDate,
          endDate: endDate,
        }
      }); // Endpoint to get candidates by status
      setHrWiseCandidates(response.data); // Assuming response.data contains an array of candidate names
      setOverallData(response.data);
    } catch (error) {
      console.error('Error fetching HR-Wise candidates:', error);
    }
  };

  const fetchData = async (mgrName, start, end) => {
    try {
      const response = await axios.get(`${URL}/api/mgr/${mgrName}/status-count`, {
        params: { startDate: start, endDate: end }  // Pass as query parameters
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchCandidatesByStatus = async (status) => {
    try {
      const response = await axios.get(`/api/candidates?status=${status}`); // Endpoint to get candidates by status
      setCandidates(response.data); // Assuming response.data contains an array of candidate names
    } catch (error) {
      console.error('Error fetching candidates:', error);
    }
  };

  const handleStatusClick = (status, names, positions) => {
    console.log('Hi Status Click');

    if (!names || !positions || names.length !== positions.length) {
      positions = names.map(() => 'No Position');
    }

    setSelectedStatus(status);

    const groupedCandidates = names.reduce((acc, name, index) => {
      const position = positions[index] || 'No Position';
      if (!acc[position]) {
        acc[position] = [];
      }
      acc[position].push(name);
      return acc;
    }, {});

    setCandidates(groupedCandidates);
    setDrawerVisible(true);
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
  };

  useEffect(() => {
    fetchHrCounts();
  }, []);

  // Handle date range change
  const handleDateChange = (dates, dateStrings) => {
    setDateRange(dates);
    setStartDate(dateStrings[0]);
    setEndDate(dateStrings[1]);

    if (selectedHrName) {
      fetchData(selectedHrName, dateStrings[0], dateStrings[1]); // Fetch filtered data
      fetchCandidatesByHR(selectedHrName, dateStrings[0], dateStrings[1]);
    }
    else {
      fetchAllCandidates(dateStrings[0], dateStrings[1]);
      fetchAllData(dateStrings[0], dateStrings[1]);
    }
  };

  // Handle download as CSV
  const handleDownload = () => {
    if (overallData.length === 0) {
      message.error("No data to download!");
      return;
    }

    // const formattedData = data.map((item) => ({
    //   Status: item.status,
    //   Count: item.count,
    //   Names: item.names ? item.names.join(', ') : 'N/A',
    //   Positions: item.positions ? item.positions.join(', ') : 'N/A',
    // }));

    let formattedData;

    if (selectedHrName) {
      formattedData = hrWiseCandidates.map((candidate, index) => ({
        Sr_No: index + 1,
        Name: candidate.fullName,
        // Email: candidate.email,
        Contact: candidate.contact,
        Position: candidate.position,
        // JobID: candidate.tatHistory[0].jobId || 'Hi',
        JobID: candidate.tatHistory && candidate.tatHistory.length > 0 ? candidate.tatHistory[0].jobId : '',
        Vertical: candidate.vertical ? candidate.vertical : '',
        Department: candidate.department ? candidate.department : '',
        Status: candidate.status,
        HR_Name: candidate.mgrName,
        Date: candidate.createdAt
          ? new Date(candidate.createdAt).toLocaleDateString("en-GB") // or "en-US"
          : "N/A",
        // Add other fields as needed like TAT, Score etc.
      }));
    } else {
      formattedData = allCandidates.map((candidate, index) => ({
        Sr_No: index + 1,
        Name: candidate.fullName,
        // Email: candidate.email,
        Contact: candidate.contact,
        Position: candidate.position,
        // JobID: candidate.tatHistory[0].jobId || 'Hi',
        JobID: candidate.tatHistory && candidate.tatHistory.length > 0 ? candidate.tatHistory[0].jobId : '',
        Vertical: candidate.vertical ? candidate.vertical : '',
        Department: candidate.department ? candidate.department : '',
        Status: candidate.status,
        HR_Name: candidate.mgrName || '',
        Date: candidate.createdAt
          ? new Date(candidate.createdAt).toLocaleDateString("en-GB") // or "en-US"
          : "N/A",
        // Add other fields as needed like TAT, Score etc.
      }));
    }


    const ws = XLSX.utils.json_to_sheet(formattedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Recruiter Data");

    const fileName = startDate && endDate
      ? `Recruiter_${selectedHrName}_${new Date(startDate).toLocaleDateString('en-GB')}_to_${new Date(endDate).toLocaleDateString('en-GB')}_Data.xlsx`
      : `Recruiter_${selectedHrName}_Data.xlsx`;

    XLSX.writeFile(wb, fileName);
    // XLSX.writeFile(wb, `Recruiter_${selectedHrName}_${new Date(startDate).toLocaleDateString('en-GB')}_to_${new Date(endDate).toLocaleDateString('en-GB')}_Data.xlsx`);
  };

  // Predefined background colors for HR cards
  const hrCardColors = ['rgb(170, 170, 170)'];
  const pieColors = ['#61acc9', '#82ca9d', '#10679b', '#42b16f', '#6ac7c9', '#4fa3c1', '#74d29c', '#1583b5', '#3cae75', '#5fc0c2']; // Define colors for the Pie chart

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', background: '#fff', padding: '20px', borderRadius: '2px', boxShadow: '0px 2px 4px rgb(38, 39, 130)' }}>

      <div style={{ width: '30%', paddingRight: '20px' }}>
        <h5 style={{ margin: '20px', fontSize: '20px', fontWeight: 'bold', color: 'rgb(26, 39, 99)' }}>Top Recruiters</h5>
        <div style={{ padding: '10px' }}>

          <ul className="hr-list">

            {paginatedHrCounts.map((hr, index) => (

              <li
                key={hr._id}
                className={`hr-card ${selectedHrName === hr._id ? 'active' : ''}`}
                style={{ backgroundColor: hrCardColors[index % hrCardColors.length] }}
                onClick={() => handleSelectHr(hr._id, hr.email)}
              >
                <div className="hr-name">{hr._id} <span className="hr-candidate-count">{hr.candidateCount}</span></div>
              </li>
            ))}
            <li></li>
          </ul>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={hrCounts.length}
            onChange={handlePageChange}
            style={{ marginTop: '20px', textAlign: 'center' }}
          />
        </div>
      </div>
      <div style={{ width: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* PieChart for Individual Data */}
        {data && data.length > 0 ? (
          <PieChart width={400} height={400}>
            <Pie
              data={data}
              dataKey="count"
              nameKey="status"
              outerRadius={125}  // Outer radius for the pie chart
              innerRadius={80}   // Inner radius to create the donut hole
              stroke="#00B4D2"
              strokeWidth={0.5}
              label
              labelLine={{ length: 20, length2: 10 }} // Adjust label line length
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        ) : (
          /* PieChart for Overall Data */
          <>
            {overallData.length === 0 ? (
              <h3 style={{ marginTop: '40%', fontWeight: 'bold' }}>No Data to Display</h3>
            ) : (
              <PieChart width={400} height={400}>
                <Pie
                  data={overallData}
                  dataKey="count"
                  nameKey="status"
                  outerRadius={125}  // Outer radius for the pie chart
                  innerRadius={80}   // Inner radius to create the donut hole
                  stroke="#00B4D2"
                  strokeWidth={0.5}
                  label
                >
                  {overallData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            )}
          </>
        )}

        {/* Custom Legend Section */}
        <div style={{
          marginTop: '2px', /* Changed: Reduced gap by 5px */
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          {(data.length > 0 ? data : overallData).map((entry, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                margin: '5px 10px',
              }}
            >
              {/* Color Box */}
              <div
                style={{
                  width: '15px',
                  height: '15px',
                  backgroundColor: pieColors[index % pieColors.length],
                  marginRight: '10px',
                  borderRadius: '3px',
                }}
              />
              {/* Label Text */}
              <span style={{ fontSize: '10px', color: '#333' }}>{entry.status}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ width: '45%' }}>
        {/* Date Range Picker */}
        <div style={{ display: 'flex', justifyContent: 'left', alignItems: 'center', marginBottom: '15px' }}>
          <RangePicker value={dateRange} style={{ height: '40px', width: '197px' }} onChange={handleDateChange} /><br />
          <Button style={{ height: '40px', marginLeft: '10px', paddingTop: '7px', borderRadius: '5px' }} type="primary" onClick={handleDownload}>Download Data</Button>
        </div>
        {data && data.length > 0 ? (
          <>
            <h5 style={{ marginBottom: '10px', fontSize: '18px', fontWeight: 'bold', color: 'rgb(26, 39, 99)' }}>
              Recruiter Name: {selectedHrName.toUpperCase()}
            </h5>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
              {/* First Column */}
              <div style={{ flex: 1 }} className='status-list'>
                {data.some((item) => item.status.includes('CV')) && (
                  <>
                    <h6 style={{ fontWeight: 'bold', color: '#333', textDecoration: "underline" }}>CV</h6>
                    <ul>
                      {data
                        .filter((item) => item.status.includes('CV'))
                        .map((item) => (
                          <li key={item.status}>
                            <a href="#!" onClick={() => handleStatusClick(item.status, item.names, item.positions)}>
                              {item.status}: {item.count}
                            </a>
                          </li>
                        ))}
                    </ul>
                  </>
                )}
                {data.some((item) => item.status.includes('L1' || 'HR')) && (
                  <>
                    <h6 style={{ fontWeight: 'bold', color: '#333', textDecoration: "underline" }}>L1</h6>
                    <ul>
                      {data
                        .filter((item) => item.status.includes('L1' || 'HR'))
                        .map((item) => (
                          <li key={item.status}>
                            <a href="#!" onClick={() => handleStatusClick(item.status, item.names, item.positions)}>
                              {item.status}: {item.count}
                            </a>
                          </li>
                        ))}
                    </ul>
                  </>
                )}
                <h6 style={{ fontWeight: 'bold', color: '#333', textDecoration: "underline" }}>L2</h6>
                <ul>
                  {data
                    .filter((item) => item.status.includes('L2'))
                    .map((item) => (
                      <li key={item.status}>
                        <a href="#!" onClick={() => handleStatusClick(item.status, item.names, item.positions)}>
                          {item.status}: {item.count}
                        </a>
                      </li>
                    ))}
                </ul>
                
               
               
              </div>

              {/* Second Column */}
              <div style={{ flex: 1 }} className='status-list'>
                {data.some((item) => item.status.includes('L3')) && (
                  <>
                    <h6 style={{ fontWeight: 'bold', color: '#333', textDecoration: "underline" }}>L3</h6>
                    <ul>
                      {data
                        .filter((item) => item.status.includes('L3'))
                        .map((item) => (
                          <li key={item.status}>
                            <a href="#!" onClick={() => handleStatusClick(item.status, item.names, item.positions)}>
                              {item.status}: {item.count}
                            </a>
                          </li>
                        ))}
                    </ul>
                  </>
                )}
                 <h6 style={{ fontWeight: 'bold', color: '#333', textDecoration: "underline" }}>L4</h6>
                <ul>
                  {data
                    .filter((item) => item.status.includes('L4'))
                    .map((item) => (
                      <li key={item.status}>
                        <a href="#!" onClick={() => handleStatusClick(item.status, item.names, item.positions)}>
                          {item.status}: {item.count}
                        </a>
                      </li>
                    ))}
                </ul>

                <h6 style={{ fontWeight: 'bold', color: '#333' }}>Other Statuses</h6>
                <ul>
                  {data
                    .filter(
                      (item) =>
                        !item.status.includes('CV') &&
                        !item.status.includes('L1') &&
                        !item.status.includes('L2') &&
                        !item.status.includes('L3') &&
                        !item.status.includes('L4')
                    )
                    .map((item) => (
                      <li key={item.status}>
                        <a href="#!" onClick={() => handleStatusClick(item.status, item.names, item.positions)}>
                          {item.status}: {item.count}
                        </a>
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </>
        ) : (
          <>
            {overallData.length === 0 ? (
              <h5 style={{ marginBottom: '10px', fontSize: '18px', fontWeight: 'bold', color: 'rgb(26, 39, 99)' }}>
                No Candidate Data to Display
              </h5>
            ) : (
              <>
                <h5 style={{ marginBottom: '10px', fontSize: '18px', fontWeight: 'bold', color: 'rgb(26, 39, 99)' }}>
                  Status with Candidates count:
                </h5>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }} className="status-list">
                  {/* First Column */}
                  <div style={{ flex: 1 }}>
                    <h6 style={{ fontWeight: 'bold', color: '#333' }}>Status with "CV"</h6>
                    <ul>
                      {overallData
                        .filter((item) => item.status.includes('CV'))
                        .map((item) => (
                          <li key={item.status}>
                            <a href="#!" onClick={() => handleStatusClick(item.status, item.names, item.positions)}>
                              {item.status}: {item.count}
                            </a>
                          </li>
                        ))}
                    </ul>

                    <h6 style={{ fontWeight: 'bold', color: '#333' }}>Status with "L1"</h6>
                    <ul>
                      {overallData
                        .filter((item) => item.status.includes('L1'))
                        .map((item) => (
                          <li key={item.status}>
                            <a href="#!" onClick={() => handleStatusClick(item.status, item.names, item.positions)}>
                              {item.status}: {item.count}
                            </a>
                          </li>
                        ))}
                    </ul>
                    <h6 style={{ fontWeight: 'bold', color: '#333' }}>Status with "L2"</h6>
                    <ul>
                      {overallData
                        .filter((item) => item.status.includes('L2'))
                        .map((item) => (
                          <li key={item.status}>
                            <a href="#!" onClick={() => handleStatusClick(item.status, item.names, item.positions)}>
                              {item.status}: {item.count}
                            </a>
                          </li>
                        ))}
                    </ul>
                  </div>

                  {/* Second Column */}
                  <div style={{ flex: 1 }} className="status-list">
                    <h6 style={{ fontWeight: 'bold', color: '#333' }}>Status with "L3"</h6>
                    <ul>
                      {overallData
                        .filter((item) => item.status.includes('L3'))
                        .map((item) => (
                          <li key={item.status}>
                            <a href="#!" onClick={() => handleStatusClick(item.status, item.names, item.positions)}>
                              {item.status}: {item.count}
                            </a>
                          </li>
                        ))}
                    </ul>
                    <h6 style={{ fontWeight: 'bold', color: '#333' }}>Status with "L4"</h6>
                    <ul>
                      {overallData
                        .filter((item) => item.status.includes('L4'))
                        .map((item) => (
                          <li key={item.status}>
                            <a href="#!" onClick={() => handleStatusClick(item.status, item.names, item.positions)}>
                              {item.status}: {item.count}
                            </a>
                          </li>
                        ))}
                    </ul>

                    <h6 style={{ fontWeight: 'bold', color: '#333' }}>Other Status</h6>
                    <ul>
                      {overallData
                        .filter(
                          (item) =>
                            !item.status.includes('CV') &&
                            !item.status.includes('L1') &&
                            !item.status.includes('L2') &&
                            !item.status.includes('L3')
                        )
                        .map((item) => (
                          <li key={item.status}>
                            <a href="#!" onClick={() => handleStatusClick(item.status, item.names, item.positions)}>
                              {item.status}: {item.count}
                            </a>
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      <Drawer
        title={`Candidates - ${selectedStatus}`}
        placement="left"
        onClose={handleDrawerClose}
        open={drawerVisible}
        width={300}
      >
        <ul>
          {Object.entries(candidates).map(([position, names], index) => (
            <li key={index}>
              {position !== 'No Position' && (
                <>
                  <strong style={{ color: "#00B4D2", fontWeight: "bold" }}>{position}</strong>  <br />
                </>
              )}
              <ul>
                {names.map((name, idx) => (
                  <li key={idx} style={{ marginLeft: "10px" }}>{name}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </Drawer>
    </div>
  );
};

export default RecruiterScorecard;